"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { convex, whenConvexReady } from '@/lib/convex-client'
import { api } from "@/convex/_generated/api"
import { useAuthStore } from './auth-store'

export type UserRole = "basic" | "essential" | "pro"

export interface RoleLimits {
  // Brain Dumping
  maxTasksPerMonth: number

  // Pomodoro Timer
  maxTimerTasksPerDay: number

  // Time Statistics & History
  maxStatsDays: number
  maxHistoryDays: number
}

export const ROLE_LIMITS: Record<UserRole, RoleLimits> = {
  basic: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: 1,
    maxStatsDays: 7,
    maxHistoryDays: 7,
  },
  essential: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: Number.POSITIVE_INFINITY,
    maxStatsDays: Number.POSITIVE_INFINITY,
    maxHistoryDays: Number.POSITIVE_INFINITY,
  },
  pro: {
    maxTasksPerMonth: Number.POSITIVE_INFINITY,
    maxTimerTasksPerDay: Number.POSITIVE_INFINITY,
    maxStatsDays: Number.POSITIVE_INFINITY,
    maxHistoryDays: Number.POSITIVE_INFINITY,
  },
}

interface UserRoleState {
  // State
  userRole: UserRole
  roleLimits: RoleLimits
  isLoading: boolean
  tasksCreatedThisMonth: number
  timerTasksToday: number
  
  // Actions
  refreshUsage: () => Promise<void>
  fetchUserRoleAndUsage: () => Promise<void>
  
  // Internal setters
  setUserRole: (role: UserRole) => void
  setIsLoading: (loading: boolean) => void
  setTasksCreatedThisMonth: (count: number) => void
  setTimerTasksToday: (count: number) => void
}

export const useUserRoleStore = create<UserRoleState>()(
  devtools(
    (set, get) => ({
      // Initial state
      userRole: "basic",
      roleLimits: ROLE_LIMITS.basic,
      isLoading: true,
      tasksCreatedThisMonth: 0,
      timerTasksToday: 0,

      // Internal setters
      setUserRole: (userRole) => set({ userRole, roleLimits: ROLE_LIMITS[userRole] }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setTasksCreatedThisMonth: (tasksCreatedThisMonth) => set({ tasksCreatedThisMonth }),
      setTimerTasksToday: (timerTasksToday) => set({ timerTasksToday }),

      // Fetch user role and usage data
      fetchUserRoleAndUsage: async () => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({ isLoading: false })
          return
        }

        try {
          set({ isLoading: true })

          // Role and usage come from Convex in one authenticated query. Period
          // boundaries are the user's local calendar, so they are computed here.
          const now = new Date()
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
          const dayStart = new Date(now)
          dayStart.setHours(0, 0, 0, 0)
          const dayEnd = new Date(dayStart)
          dayEnd.setDate(dayEnd.getDate() + 1)

          await whenConvexReady()

          const usage = await convex.query(api.limits.currentUsage, {
            monthStart: monthStart.getTime(),
            monthEnd: monthEnd.getTime(),
            dayStart: dayStart.getTime(),
            dayEnd: dayEnd.getTime(),
          })

          if (usage.role === "basic" || usage.role === "essential" || usage.role === "pro") {
            get().setUserRole(usage.role as UserRole)
          }

          set({
            tasksCreatedThisMonth: usage.tasksCreatedThisMonth,
            timerTasksToday: usage.timerTasksToday,
          })
        } catch (error) {
          console.error("Error in fetchUserRoleAndUsage:", error)
        } finally {
          set({ isLoading: false })
        }
      },

      refreshUsage: async () => {
        // This is essentially the same as fetchUserRoleAndUsage but focuses on usage data
        await get().fetchUserRoleAndUsage()
      },


    }),
    { name: 'user-role-store' }
  )
)
